/**
 * BHG Unified Analytics
 * ---------------------
 * Single source of truth for Meta Pixel, Reddit Pixel, GA4, Google Ads, and
 * Plausible tracking.
 *
 * Pixel IDs are read from Vite env at build time:
 *   VITE_META_PIXEL_ID     — Meta (Facebook/Instagram) Pixel ID
 *   VITE_REDDIT_PIXEL_ID   — Reddit Pixel ID (a2_xxxxxxxxx) — Phase 10b
 *   VITE_GA4_ID            — GA4 measurement ID (G-XXXXXXXXXX)
 *   VITE_GADS_ID           — Google Ads account ID (AW-XXXXXXXXX)
 *   VITE_GADS_CONVERSION   — default Google Ads conversion label (AW-XXXX/abcDEF)
 *
 * All calls are safe before init() runs — they queue into dataLayer or no-op.
 * Respects Global Privacy Control (navigator.globalPrivacyControl === true) and
 * navigator.doNotTrack === "1": both skip all external pixels, GA4 and the CAPI
 * mirrors, and keep first-party Supabase tracking working. Meta and Reddit never
 * get compound or condition names (see "What the advertising pixels may see").
 *
 * Phase 10a/10b dedup: every track() call generates ONE event_id (UUID) shared
 * by browser pixel + server CAPI. Meta dedups via fbq's eventID parameter.
 * Reddit dedups via rdt's conversionId parameter and the matching conversion_id
 * in the reddit-capi edge function. Within Reddit's attribution window the
 * deduped event gets ~2x weight in their bidding optimizer.
 */

// ---------- Types ----------

export type BhgEvent =
  | "view_content"
  | "view_vendor"
  | "lead"
  | "affiliate_click"
  | "add_to_cart" // alias of affiliate_click for Meta ad-optimization
  | "initiate_checkout"
  | "purchase"
  | "signup"
  | "search"
  | "tool_used"
  | "download";

export interface TrackProps {
  /** Logical identifier: compound slug, vendor slug, tool slug */
  content_id?: string;
  /** Human label */
  content_name?: string;
  /** Broad category (peptide, nootropic, tool, guide, vendor) */
  content_category?: string;
  /** Monetary value (USD) — commissions or product price for Meta VALUE-based bidding */
  value?: number;
  currency?: string;
  /** Free-form source label (e.g. "cheat-sheet-hero", "compound-page-bpc157") */
  source?: string;
  /** Additional props forwarded to each pixel */
  [key: string]: unknown;
}

// ---------- Globals ----------

declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
    _fbq?: (...args: any[]) => void;
    rdt?: ((...args: any[]) => void) & {
      callQueue?: any[];
      sendEvent?: (...args: any[]) => void;
    };
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
    plausible?: (event: string, opts?: { props?: Record<string, unknown> }) => void;
  }
}

const env = (import.meta as any).env || {};

const META_PIXEL_ID: string | undefined = env.VITE_META_PIXEL_ID;
// Hardcoded fallback (same rationale as GA4_ID below): Lovable rejects VITE_*
// Cloud Secrets and `.env` is gitignored, so VITE_REDDIT_PIXEL_ID never reaches
// the build runner. Without this fallback the Reddit browser pixel never loaded
// on the main site (only the standalone /ad-lander.html hardcoded it) — which is
// exactly why Reddit's Events Manager showed almost all Leads arriving via CAPI
// with no pixel to dedup against, and flagged "match quality: needs attention".
// Reddit pixel IDs are PUBLIC (baked into the bundle), so hardcoding has no
// security cost. Override via env var if a future stream needs a different ID.
const REDDIT_PIXEL_ID: string | undefined =
  env.VITE_REDDIT_PIXEL_ID || "a2_hoc25w1vj7yq";
// GA4 measurement ID. Hardcoded fallback because Lovable rejects VITE_*
// names in Cloud Secrets ("Invalid secret name… VITE_ prefixed variables
// are build-time browser values and should be defined in .env files,
// not in secrets") and `.env` / `.env.*` are gitignored, so neither
// surface reaches Lovable's build runner. GA4 measurement IDs are
// PUBLIC (baked into the bundle and visible to every visitor) so
// hardcoding has no security cost. Override via env var if a future
// dev/staging stream needs a different ID.
const GA4_ID: string | undefined = env.VITE_GA4_ID || "G-PBHMRQJF1N";
const GADS_ID: string | undefined = env.VITE_GADS_ID;
const GADS_CONVERSION: string | undefined = env.VITE_GADS_CONVERSION;

// 2026-05-13: SUPABASE_URL imported from canonical client module (audit fix #8).
// Was duplicated here for ~6 months. The client module still uses
// env.VITE_SUPABASE_URL with the same hardcoded fallback.
import { SUPABASE_URL } from "@/integrations/supabase/client";
import { affiliateClickParams, classifyAffiliateLink, type AffiliateClickInput } from "@/lib/affiliateClick";
import { getFirstTouch } from "@/lib/firstTouch";
import { pageCategory } from "@/lib/pageCategory";

let initialized = false;

/**
 * The trust badge embed (/embed/trust-badge) is an iframe on vendors' own sites. The plugin
 * docs tell those vendors it loads no advertising pixels
 * (wordpress-plugin/bhg-verified-badge/readme.txt), so nothing in this file may run there:
 * no pixel loaders, no events, no server-side mirror. Change the docs before changing this.
 */
function isPixelFreeRoute(): boolean {
  return window.location.pathname.startsWith("/embed/trust-badge");
}

/**
 * Global Privacy Control: the browser signal that the reader opts out of the sale and sharing of their data, which
 * California and several other states treat as a binding opt-out. With it on, nothing in this file runs: no pixel
 * loads, no event is sent and no server-side mirror is called. Unlike Do Not Track it has no exception for automated
 * browsers.
 */
function hasGlobalPrivacyControl(): boolean {
  try {
    return (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
  } catch {
    return false;
  }
}

function isTrackingAllowed(): boolean {
  if (typeof window === "undefined") return false;
  if (isPixelFreeRoute()) return false;
  if (hasGlobalPrivacyControl()) return false;
  // Honor DNT but keep bot/test traffic tracked (navigator.webdriver is true).
  if (navigator.doNotTrack === "1" && !(navigator as any).webdriver) return false;
  return true;
}

// ---------- What the advertising pixels may see ----------
//
// Meta and Reddit, in the browser and through the meta-capi and reddit-capi mirrors, get no compound or condition
// names: no content_name, no content_id, no search text, no source label, no page path. California fined Healthline
// $1.55 million in 2025 over article titles that suggested a diagnosis being sent to ad trackers, and an event field
// such as "retatrutide" or a /compound/retatrutide URL is the same kind of signal. What they get instead:
//   - content_ids: the page type (lib/pageCategory.ts: compound_page, tool, guide, ...);
//   - content_category: the caller's category when it is one of the generic ones below (they tell the ad optimizers
//     a lead magnet from a newsletter signup or a quality visit), else the page type;
//   - value and currency, and the dedup event_id, unchanged;
//   - event_source_url: the site's origin, without the path.
// The pixels still read the page URL themselves when they load; only Global Privacy Control, Do Not Track or a
// consent gate keeps that from them. GA4 is analytics, not advertising, and keeps its full parameters.
const GENERIC_PIXEL_CATEGORIES = new Set([
  "affiliate",
  "coaching",
  "data",
  "guide",
  "lead-magnet",
  "lp-cta",
  "newsletter",
  "partner_vendor",
  "protocol",
  "quiz",
  "tool",
  "tool-deliverable",
  "vendor",
  "quality-visit",
  "quality-visit-compare",
  "ad-lp-compare",
  "ad-lp-tool",
]);

function pixelContext(props: TrackProps): { pageType: string; category: string; sourceUrl: string } {
  const pageType = pageCategory(window.location.pathname);
  const asked = typeof props.content_category === "string" ? props.content_category : "";
  return {
    pageType,
    category: GENERIC_PIXEL_CATEGORIES.has(asked) ? asked : pageType,
    sourceUrl: `${window.location.origin}/`,
  };
}

// ---------- Pixel loaders ----------

function loadMetaPixel(id: string) {
  // Standard Meta pixel snippet, minified.
  /* eslint-disable */
  (function (f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */
  // Before init: no automatic events and no page metadata (titles, Open Graph and schema data), which name compounds.
  window.fbq?.("set", "autoConfig", false, id);
  window.fbq?.("init", id);
  window.fbq?.("track", "PageView");
}

/**
 * Reddit Pixel — required for browser-side dedup against /reddit-capi.
 *
 * Reddit uses `conversionId` on rdt('track', ...) calls to dedup against the
 * `conversion_id` we send via reddit-capi edge function. Without the browser
 * pixel, CAPI still works but Reddit can't cross-reference the two — the
 * deduped events get the higher 2x weight in their bidding optimizer.
 *
 * The pixel auto-sets the rdt_uuid cookie on load, which our analytics also
 * forwards to the CAPI for additional user matching beyond IP/UA.
 */
function loadRedditPixel(id: string) {
  /* eslint-disable */
  (function (w: any, d: any) {
    if (!w.rdt) {
      var p: any = (w.rdt = function () {
        p.sendEvent
          ? p.sendEvent.apply(p, arguments)
          : p.callQueue.push(arguments);
      });
      p.callQueue = [];
      var t = d.createElement("script");
      t.src = "https://www.redditstatic.com/ads/pixel.js";
      t.async = !0;
      var s = d.getElementsByTagName("script")[0];
      s.parentNode.insertBefore(t, s);
    }
  })(window, document);
  /* eslint-enable */
  window.rdt?.("init", id);
  window.rdt?.("track", "PageVisit");
}

function loadGtag(ids: string[]) {
  if (!ids.length) return;
  // Inject the first id's loader script; gtag supports multiple ids.
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ids[0])}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer!.push(arguments);
  };
  window.gtag("js", new Date());
  for (const id of ids) {
    window.gtag("config", id, { send_page_view: true });
  }
}

// ---------- Init ----------

/**
 * Initialize all configured pixels. Safe to call multiple times.
 * Should be called once from main.tsx on app boot.
 */
export function initAnalytics() {
  if (initialized) return;
  if (typeof window === "undefined") return;
  if (!isTrackingAllowed()) return;

  try {
    if (META_PIXEL_ID) loadMetaPixel(META_PIXEL_ID);
  } catch (err) {
    console.warn("[analytics] Meta pixel init failed:", err);
  }

  try {
    if (REDDIT_PIXEL_ID) loadRedditPixel(REDDIT_PIXEL_ID);
  } catch (err) {
    console.warn("[analytics] Reddit pixel init failed:", err);
  }

  try {
    const ids = [GA4_ID, GADS_ID].filter(Boolean) as string[];
    if (ids.length) loadGtag(ids);
  } catch (err) {
    console.warn("[analytics] gtag init failed:", err);
  }

  try {
    listenForAffiliateClicks();
  } catch (err) {
    console.warn("[analytics] affiliate click listener failed:", err);
  }

  initialized = true;
}

// ---------- GA4 affiliate_click ----------
//
// One GA4 event per outbound affiliate or store click, with vendor, compound, placement, link_domain and the reader's
// first-touch source and medium (lib/affiliateClick.ts builds the parameters). Two senders:
//   - the click helpers (trackAffiliateClick below, trackBuyClick in lib/trackClick.ts), which know the vendor and the
//     compound;
//   - a document click listener for links no helper covers: the site's own /go/<vendor> links (GA4's built-in
//     outbound-click event does not fire on that same-domain hop), store links and other rel="sponsored" links.
// The listener has two halves around each click. The capture half notes the link before any handler runs, so a helper
// can read its URL; the bubble half runs after the handlers and sends the event only if no helper sent one for that
// click. Pixel-free routes, Global Privacy Control and Do Not Track stop it like every other event (isTrackingAllowed).
let currentClick: { href: string; rel: string | null; sent: boolean } | null = null;
let listeningForClicks = false;

export function trackAffiliateClickGA4(click: AffiliateClickInput & { rel?: string | null }): void {
  if (typeof window === "undefined" || !isTrackingAllowed()) return;
  const href = click.href ?? currentClick?.href ?? null;
  const rel = click.rel ?? currentClick?.rel ?? null;
  if (currentClick) currentClick.sent = true;
  try {
    window.gtag?.(
      "event",
      "affiliate_click",
      affiliateClickParams(
        { ...click, href },
        { pathname: window.location.pathname, origin: window.location.origin, firstTouch: getFirstTouch(), rel },
      ),
    );
  } catch (err) {
    console.warn("[analytics] affiliate_click failed:", err);
  }
}

function clickedLink(event: Event): HTMLAnchorElement | null {
  const target = event.target as Element | null;
  return (target?.closest?.("a[href]") as HTMLAnchorElement | null) ?? null;
}

function listenForAffiliateClicks(): void {
  if (listeningForClicks || typeof document === "undefined") return;
  listeningForClicks = true;
  document.addEventListener(
    "click",
    (event) => {
      const link = clickedLink(event);
      currentClick = link ? { href: link.href, rel: link.getAttribute("rel"), sent: false } : null;
    },
    true,
  );
  const sendForUncoveredLink = (event: Event) => {
    const link = clickedLink(event);
    const click = currentClick;
    currentClick = null;
    if (!link || click?.sent) return;
    const rel = link.getAttribute("rel");
    const info = classifyAffiliateLink(link.href, rel, window.location.origin);
    if (!info) return;
    trackAffiliateClickGA4({ vendor: info.vendor, placement: info.placement, href: link.href, rel });
  };
  document.addEventListener("click", sendForUncoveredLink);
  // A middle click opens the link in a new tab with no click event, so no helper runs for it.
  document.addEventListener("auxclick", (event) => {
    if ((event as MouseEvent).button !== 1) return;
    currentClick = null;
    sendForUncoveredLink(event);
  });
}

// ---------- Event mapping ----------

/**
 * Translate our unified event name to each pixel's native event name.
 * - Meta: https://developers.facebook.com/docs/meta-pixel/reference
 * - GA4: use event name as-is (supports custom events)
 */
const META_EVENT_MAP: Record<BhgEvent, string | null> = {
  view_content: "ViewContent",
  view_vendor: "ViewContent", // treat as ViewContent with content_category=vendor
  lead: "Lead",
  affiliate_click: "AddToCart", // best proxy for revenue-intent click
  add_to_cart: "AddToCart",
  initiate_checkout: "InitiateCheckout",
  purchase: "Purchase",
  signup: "CompleteRegistration",
  search: "Search",
  tool_used: "CustomizeProduct",
  download: "Lead", // gated download == lead
};

// ---------- CAPI helpers ----------
//
// Phase 10a: server-side mirror of pixel events via /meta-capi and /reddit-capi
// edge functions. Browser pixels lose ~30-70% of events to ATT, ITP, ad
// blockers, and aggressive privacy extensions. CAPI fires server-side with
// the same event_id so Meta + Reddit dedup against the pixel and weight the
// merged event ~2x higher in their bidding optimizer.
//
// All CAPI calls are fire-and-forget (no await): the function returns
// immediately and the fetch promise resolves in the background. CAPI failure
// must never block UX or pixel firing.

/**
 * Generate a UUIDv4 for use as the pixel ↔ CAPI dedup event_id.
 * Falls back to a Math.random-based value on browsers without crypto.randomUUID.
 */
function generateEventId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Fallback for older Safari / non-secure contexts. Not RFC4122-perfect but
  // good enough for dedup; collision probability is astronomically low.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Deterministic, dedup-friendly conversion id for Lead/SignUp events that have
 * an email. Both the browser (via captureEmail → props._eventId) and the
 * Beehiiv → reddit-capi webhook compute `bhg_lead_<sha256(lowercased,trimmed
 * email)>`, so the same signup fired from either side collapses into ONE Reddit
 * conversion instead of double-counting. Reddit takes conversion_id as a plain
 * string; a SHA-256 of the email keeps it opaque + non-PII (the email itself
 * still rides separately as the hashed user.email match key). Requires a secure
 * context (crypto.subtle); rejects → caller falls back to a random id.
 */
export async function leadDedupEventId(email: string): Promise<string> {
  const buf = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(email.trim().toLowerCase()),
  );
  const hex = Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return `bhg_lead_${hex}`;
}

/**
 * Read a cookie value by name. Returns undefined if missing or in non-browser env.
 */
function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : undefined;
}

/**
 * Read a URL search param. Returns undefined if missing.
 */
function readUrlParam(name: string): string | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    return new URLSearchParams(window.location.search).get(name) ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * Pull Meta pixel cookies (_fbp, _fbc) for inclusion in CAPI user_data.
 * _fbp is the persistent pixel ID; _fbc encodes the most recent fbclid.
 * Both are auto-managed by the Meta pixel script.
 */
function getMetaUserData(): { fbp?: string; fbc?: string } {
  return {
    fbp: readCookie("_fbp"),
    fbc: readCookie("_fbc"),
  };
}

/**
 * Pull Reddit pixel cookie (rdt_uuid) + click ID URL param (rdt_cid).
 * Reddit sets rdt_uuid when the pixel loads (which now happens site-wide via
 * the hardcoded REDDIT_PIXEL_ID); rdt_cid arrives as a URL param on ad clicks.
 * These plus a hashed email (on Lead/SignUp events) are the match keys the
 * CAPI forwards so Reddit can attribute conversions to real users.
 */
function getRedditUserData(): { rdt_uuid?: string; rdt_cid?: string } {
  return {
    rdt_uuid: readCookie("rdt_uuid"),
    rdt_cid: readUrlParam("rdt_cid"),
  };
}

/**
 * Fire a single CAPI event server-side. Fire-and-forget — returns void.
 * Errors are swallowed and console.warned to keep tracking failures from
 * cascading into UX bugs. The /meta-capi and /reddit-capi edge functions
 * are no-op when their respective env vars aren't configured (returns
 * { ok: false, reason: "capi_not_configured" } with HTTP 200).
 */
function fireCAPI(
  source: "meta" | "reddit",
  payload: {
    event_name: string;
    event_id: string;
    event_time: number;
    event_source_url: string;
    value?: number;
    currency?: string;
    content_ids?: string[];
    content_category?: string;
    user_data?: Record<string, unknown>;
  }
): void {
  if (typeof window === "undefined" || !isTrackingAllowed()) return;

  const url = `${SUPABASE_URL}/functions/v1/${source}-capi`;
  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    keepalive: true, // keeps the request alive past navigation/unload
    credentials: "omit", // no cookies — keep CORS simple
  }).catch((err) => {
    console.warn(`[analytics] ${source}-capi fire failed:`, err);
  });
}

// ---------- Core tracker ----------

export function track(event: BhgEvent, props: TrackProps = {}) {
  if (typeof window === "undefined") return;
  if (!isTrackingAllowed()) return;

  // event_id is shared between the browser pixel + server CAPI so they dedup.
  // Reddit requires conversion_id to be an unhashed string. Callers MAY pass a
  // deterministic `_eventId` (bhg_lead_<sha256(email)>, via captureEmail) so a
  // browser Lead and the Beehiiv-webhook Lead for the same person collapse into
  // ONE Reddit conversion instead of double-counting; otherwise we mint a
  // random per-call id below.
  const event_time = Math.floor(Date.now() / 1000);
  // What Meta and Reddit may see: generic categories, the site's origin (see "What the advertising pixels may see").
  const pixel = pixelContext(props);
  const event_source_url = pixel.sourceUrl;
  const pixelContentIds = props.content_id ? [pixel.pageType] : undefined;

  // Advanced matching: pull `email` OUT of props so it powers pixel + CAPI
  // identity matching (user_data.em / rdt advanced matching) but NEVER leaks
  // into GA4/Plausible/pixel event params. Passing a hashed email on Lead/
  // SignUp events is what raises Reddit's "Low email coverage" match-quality
  // score. Callers (captureEmail) pass `email` only when they legitimately
  // have it (a form submission). The CAPI edge functions hash it server-side;
  // the Reddit browser pixel hashes it client-side.
  const { email: _leadEmailRaw, _eventId: _overrideEventId, ...trackProps } =
    props as TrackProps & { email?: unknown; _eventId?: unknown };
  const leadEmail =
    typeof _leadEmailRaw === "string" && _leadEmailRaw.includes("@")
      ? _leadEmailRaw.trim().toLowerCase()
      : undefined;
  // Deterministic dedup id when a caller supplied one (Lead/SignUp with email),
  // else a random per-call id. Extracted from props so it never reaches the
  // pixel/GA4/Plausible event params.
  const event_id =
    typeof _overrideEventId === "string" && _overrideEventId
      ? _overrideEventId
      : generateEventId();

  // Meta Pixel
  try {
    const metaEvent = META_EVENT_MAP[event];
    if (metaEvent && window.fbq) {
      // Only generic fields: no content_name, content_id, source label or search text (they name compounds).
      const metaProps: Record<string, unknown> = { content_category: pixel.category };
      if (pixelContentIds) metaProps.content_ids = pixelContentIds;
      if (props.value != null) metaProps.value = props.value;
      metaProps.currency = props.currency || "USD";
      // Pass eventID so Meta CAPI dedup works. Without this, the same
      // conversion gets counted twice (once from pixel, once from CAPI).
      window.fbq("track", metaEvent, metaProps, { eventID: event_id });
    }
  } catch (err) {
    console.warn("[analytics] Meta track failed:", err);
  }

  // Reddit Pixel (browser-side) — fires the same event_id as conversionId so
  // the reddit-capi server fire dedups against this. Reddit standard events:
  // PageVisit, ViewContent, Search, AddToCart, Purchase, Lead, SignUp.
  // We map our internal events to the closest Reddit standard.
  try {
    if (window.rdt) {
      // Advanced matching: re-init the pixel with the email so Reddit can match
      // this + subsequent events to a Reddit user. Reddit hashes it client-side;
      // plaintext never leaves the user's own browser. This is the browser-side
      // half of the "Low email coverage" fix (the CAPI half is user_data.em).
      if (leadEmail && REDDIT_PIXEL_ID) {
        try {
          window.rdt("init", REDDIT_PIXEL_ID, { email: leadEmail });
        } catch {
          /* noop */
        }
      }
      const REDDIT_BROWSER_EVENT_MAP: Record<BhgEvent, string> = {
        view_content: "ViewContent",
        view_vendor: "ViewContent",
        lead: "Lead",
        affiliate_click: "AddToCart",
        add_to_cart: "AddToCart",
        initiate_checkout: "AddToCart",
        purchase: "Purchase",
        signup: "SignUp",
        search: "Search",
        tool_used: "ViewContent",
        download: "Lead",
      };
      const redditEvent = REDDIT_BROWSER_EVENT_MAP[event];
      if (redditEvent) {
        const redditProps: Record<string, unknown> = {
          conversionId: event_id, // CRITICAL: this matches reddit-capi conversion_id (DO NOT HASH)
        };
        // Currency is ONLY valid alongside a value. Sending it bare made Reddit
        // flag "Unsupported metadata" on 100% of ViewContent (156/156) and ~50%
        // of Lead events, which drags Match Quality down and weakens Reddit's
        // ability to find buyers. Reddit does not accept `currency` on
        // ViewContent at all, and a currency with nothing to denominate is
        // meaningless on every other event too — so gate them together.
        if (props.value != null) {
          redditProps.value = props.value;
          redditProps.currency = props.currency || "USD";
        }
        // The page type, never the compound: see "What the advertising pixels may see".
        if (pixelContentIds) {
          redditProps.products = [{ id: pixel.pageType, category: pixel.category }];
        }
        window.rdt("track", redditEvent, redditProps);
      }
    }
  } catch (err) {
    console.warn("[analytics] Reddit pixel track failed:", err);
  }

  // Meta CAPI (server-side mirror) — fire-and-forget
  try {
    const metaEventName = META_EVENT_MAP[event];
    if (metaEventName) {
      fireCAPI("meta", {
        event_name: metaEventName,
        event_id,
        event_time,
        event_source_url,
        value: props.value,
        currency: props.currency || "USD",
        content_ids: pixelContentIds,
        content_category: pixel.category,
        user_data: {
          ...getMetaUserData(),
          ...(leadEmail ? { em: [leadEmail] } : {}),
        },
      });
    }
  } catch (err) {
    console.warn("[analytics] meta-capi fire failed:", err);
  }

  // Reddit CAPI (server-side mirror) — fire-and-forget. Dedups against the
  // browser pixel via the shared event_id (conversionId ↔ conversion_id). On
  // ad clicks the rdt_cid URL param + rdt_uuid cookie are forwarded as match
  // keys, and Lead/SignUp events also carry the hashed email (user_data.em).
  try {
    // Use our internal event names directly. The reddit-capi edge function
    // maps to Reddit's tracking_type vocabulary on the server side.
    const redditEventName: Record<BhgEvent, string> = {
      view_content: "ViewContent",
      view_vendor: "ViewContent",
      lead: "Lead",
      affiliate_click: "AffiliateClick",
      add_to_cart: "AddToCart",
      initiate_checkout: "AddToCart",
      purchase: "Purchase",
      signup: "CompleteRegistration",
      search: "Search",
      tool_used: "ViewContent",
      download: "Lead",
    };
    fireCAPI("reddit", {
      event_name: redditEventName[event],
      event_id,
      event_time,
      event_source_url,
      value: props.value,
      // Only meaningful with a value — see the pixel branch above. The
      // reddit-capi edge function also guards this server-side
      // (`body.value != null ? ... : undefined`), so this is belt-and-braces:
      // it stops an unsupported field being sent even if that guard is ever
      // relaxed.
      currency: props.value != null ? props.currency || "USD" : undefined,
      content_ids: pixelContentIds,
      content_category: pixel.category,
      user_data: {
        ...getRedditUserData(),
        ...(leadEmail ? { em: [leadEmail] } : {}),
      },
    });
  } catch (err) {
    console.warn("[analytics] reddit-capi fire failed:", err);
  }

  // GA4 / Google Ads (via gtag). affiliate_click is the exception: it reaches GA4 once, through
  // trackAffiliateClickGA4(), with its own parameters.
  try {
    if (window.gtag && event !== "affiliate_click") {
      window.gtag("event", event, {
        ...trackProps,
        currency: props.currency || "USD",
      });

      // Fire Google Ads conversion if event is high-intent and conversion label configured
      if (GADS_CONVERSION && (event === "lead" || event === "purchase" || event === "signup")) {
        window.gtag("event", "conversion", {
          send_to: GADS_CONVERSION,
          value: props.value || 0,
          currency: props.currency || "USD",
        });
      }
    }
  } catch (err) {
    console.warn("[analytics] gtag track failed:", err);
  }

  // Plausible (first-party, always runs)
  try {
    if (window.plausible) {
      window.plausible(event, { props: trackProps as Record<string, unknown> });
    }
  } catch {
    // noop
  }
}

// ---------- Convenience helpers ----------

export const trackViewContent = (props: TrackProps) => track("view_content", props);
export const trackViewVendor = (props: TrackProps) =>
  track("view_vendor", { content_category: "vendor", ...props });
export const trackLead = (props: TrackProps) => track("lead", props);
/**
 * An outbound affiliate or store click. Sends the GA4 affiliate_click event (vendor from `vendor` or `vendor_name`,
 * placement from `placement` or `source`, the compound from `compound` or the page) and the pixel events. trackBuyClick
 * sends its GA4 event itself, before its value lookup, and passes { ga4: false }.
 */
export const trackAffiliateClick = (props: TrackProps, options: { ga4?: boolean } = {}) => {
  if (options.ga4 !== false) {
    trackAffiliateClickGA4({
      vendor: props.vendor ?? props.vendor_name,
      compound: props.compound,
      placement: props.placement ?? props.source,
      href: typeof props.href === "string" ? props.href : undefined,
      value: props.value,
    });
  }
  // Fire both for Meta optimizer flexibility
  track("affiliate_click", props);
  track("add_to_cart", props);
};
export const trackPurchase = (props: TrackProps) => track("purchase", props);
export const trackSignup = (props: TrackProps) => track("signup", props);
export const trackSearch = (query: string) => track("search", { search_string: query });
export const trackToolUsed = (toolSlug: string, props: TrackProps = {}) =>
  track("tool_used", { content_id: toolSlug, content_category: "tool", ...props });
export const trackDownload = (props: TrackProps) => track("download", props);

/**
 * Fire a page_view event. Called from a global route-change listener.
 */
export function trackPageView(path: string) {
  if (typeof window === "undefined") return;
  if (!isTrackingAllowed()) return;
  try {
    window.fbq?.("track", "PageView");
    window.rdt?.("track", "PageVisit");
    window.gtag?.("event", "page_view", { page_path: path });
    // Plausible auto-tracks pageviews via its script tag, so no call here.
  } catch (err) {
    console.warn("[analytics] page_view failed:", err);
  }
}
